import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { productsService } from '../../../services/products';
import { recommendationsService } from '../../../services/recommendations';

const TrendingSection = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [trendingGifts, setTrendingGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const categories = [
    { id: 'all', name: 'All Trending', icon: 'TrendingUp' },
    { id: 'experiences', name: 'Experiences', icon: 'MapPin' },
    { id: 'personalized', name: 'Personalized', icon: 'Heart' },
    { id: 'tech', name: 'Tech & Gadgets', icon: 'Smartphone' },
    { id: 'wellness', name: 'Wellness', icon: 'Flower' }
  ];

  // Mock data as fallback
  const mockTrendingGifts = [
    {
      id: 1,
      title: "Custom Star Map",
      category: "personalized",
      price: "$49",
      image: "https://images.unsplash.com/photo-1446776877081-d282a0f896e2?w=400&h=300&fit=crop",
      description: "Personalized star map of any special date",
      trending: "+127% this week",
      likes: 2847,
      saves: 1203,
      tags: ["Anniversary", "Romantic", "Personalized"]
    },
    {
      id: 2,
      title: "Cooking Class Experience",
      category: "experiences",
      price: "$89",
      image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop",
      description: "Learn to cook with professional chefs",
      trending: "+89% this week",
      likes: 1924,
      saves: 856,
      tags: ["Experience", "Food", "Learning"]
    },
    {
      id: 3,
      title: "Smart Plant Monitor",
      category: "tech",
      price: "$34",
      image: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&h=300&fit=crop",
      description: "Keep plants healthy with smart monitoring",
      trending: "+156% this week",
      likes: 3421,
      saves: 1876,
      tags: ["Tech", "Plants", "Smart Home"]
    },
    {
      id: 4,
      title: "Aromatherapy Diffuser Set",
      category: "wellness",
      price: "$67",
      image: "https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&h=300&fit=crop",
      description: "Premium essential oils with elegant diffuser",
      trending: "+203% this week",
      likes: 2156,
      saves: 1432,
      tags: ["Wellness", "Relaxation", "Home"]
    },
    {
      id: 5,
      title: "Wine Tasting Experience",
      category: "experiences",
      price: "$125",
      image: "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=400&h=300&fit=crop",
      description: "Guided wine tasting at local vineyard",
      trending: "+78% this week",
      likes: 1687,
      saves: 923,
      tags: ["Experience", "Wine", "Date Night"]
    },
    {
      id: 6,
      title: "Wireless Charging Station",
      category: "tech",
      price: "$45",
      image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop",
      description: "Charge multiple devices simultaneously",
      trending: "+112% this week",
      likes: 2934,
      saves: 1654,
      tags: ["Tech", "Practical", "Office"]
    }
  ];

  // Load trending products from API
  const loadTrendingProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to get trending recommendations first
      let data;
      try {
        data = await recommendationsService.getTrendingProducts();
      } catch (recommendationError) {
        console.log('Recommendations API not available, falling back to products API');
        // Fallback to products API with trending/popular parameters
        data = await productsService.getProducts({
          ordering: '-popularity', // or whatever field indicates trending
          limit: 20
        });
      }

      // Transform API data to match expected format
      const transformedData = data.results || data;

      // If we have real product data, transform it to match the expected format
      if (transformedData && transformedData.length > 0) {
        const formattedProducts = transformedData.slice(0, 6).map((product, index) => {
          // Debug logging to see what image fields are available
          console.log('Product image fields:', {
            image_url: product.image_url,
            image: product.image,
            images: product.images,
            thumbnail: product.thumbnail,
            productId: product.id
          });

          return {
            id: product.id,
            title: product.name || product.title,
            category: product.category?.slug || 'all',
            price: product.retail_price || product.price ? `$${product.retail_price || product.price}` : '$0',
            image: product.images && product.images.length > 0 ? product.images[0].image || product.images[0] : `https://images.unsplash.com/photo-${1446776877081 + index}-d282a0f896e2?w=400&h=300&fit=crop`,
            description: product.description || product.short_description || 'Great gift option',
            trending: `+${Math.floor(Math.random() * 200) + 50}% this week`,
            likes: Math.floor(Math.random() * 5000) + 1000,
            saves: Math.floor(Math.random() * 2000) + 500,
            tags: product.tags || []
          };
        });
        setTrendingGifts(formattedProducts);
      } else {
        // Fallback to mock data if no products returned
        setTrendingGifts(mockTrendingGifts);
      }
    } catch (err) {
      setError('Failed to load trending products.');
      console.error('Error loading trending products:', err);
      // Fallback to mock data if API fails
      setTrendingGifts(mockTrendingGifts);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrendingProducts();
  }, []);

  const filteredGifts = activeCategory === 'all'
    ? trendingGifts
    : trendingGifts.filter(gift => gift.category === activeCategory);

  const handleGiftClick = (gift) => {
    navigate('/gift-finder', { state: { selectedGift: gift } });
  };

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-primary-50 rounded-full px-4 py-2 mb-4">
            <Icon name="TrendingUp" size={16} className="text-primary" />
            <span className="text-sm font-medium text-primary">Trending Now</span>
          </div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-accent-800 mb-4">
            What's Popular This Week
          </h2>
          <p className="text-lg text-accent-600 max-w-2xl mx-auto">
            Discover the gifts that are making people smile across our community
          </p>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-full font-medium text-sm transition-all duration-300 ${activeCategory === category.id
                ? 'bg-primary text-white shadow-primary'
                : 'bg-surface text-accent-600 hover:bg-primary-50 hover:text-primary'
                }`}
            >
              <Icon name={category.icon} size={16} />
              <span>{category.name}</span>
            </button>
          ))}
        </div>

        {/* Trending Gifts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredGifts.map((gift) => (
            <div
              key={gift.id}
              onClick={() => handleGiftClick(gift)}
              className="group bg-white rounded-2xl shadow-soft hover:shadow-warm border border-accent-100 overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1"
            >
              {/* Gift Image */}
              <div className="relative overflow-hidden bg-white" style={{ height: '192px' }}>
                <Image
                  src={gift.image}
                  alt={gift.title}
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-3 left-3 bg-success text-white text-xs font-medium px-2 py-1 rounded-full">
                  {gift.trending}
                </div>
                <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <Icon name="Heart" size={16} className="text-accent-600 hover:text-primary transition-colors" />
                </div>
              </div>

              {/* Gift Content */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-heading text-lg font-semibold text-accent-800 group-hover:text-primary transition-colors">
                    {gift.title}
                  </h3>
                  <span className="font-bold text-primary text-lg">
                    {gift.price}
                  </span>
                </div>

                <p className="text-accent-600 text-sm mb-4 line-clamp-2">
                  {gift.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-1 mb-4">
                  {gift.tags.slice(0, 3).map((tag, index) => (
                    <span
                      key={index}
                      className="text-xs bg-accent-100 text-accent-600 px-2 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Engagement Stats */}
                <div className="flex items-center justify-between text-sm text-accent-500">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1">
                      <Icon name="Heart" size={14} />
                      <span>{gift.likes.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Bookmark" size={14} />
                      <span>{gift.saves.toLocaleString()}</span>
                    </div>
                  </div>
                  <Icon name="ArrowRight" size={16} className="text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center">
          <Button
            variant="outline"
            size="lg"
            iconName="ArrowRight"
            iconPosition="right"
            onClick={() => navigate('/gift-finder')}
            className="border-accent-200 hover:border-primary-300 hover:bg-primary-50 transition-all duration-300"
          >
            Explore All Trending Gifts
          </Button>
        </div>
      </div>
    </section>
  );
};

export default TrendingSection;